import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import LuigisboxAutocomplete from "../../luigisbox/LuigisboxAutocomplete";
import elementService from "@/common/ElementService";

interface HeaderSearchInputProperties {
    readonly trackerId: string;
    readonly hostname: string;
    readonly initialSearchQuery: string;
}

const usePlaceholderTyping = (placeholderText: string): string => {
    const [placeholder, setPlaceholder] = useState("");

    useEffect(() => {
        let count = 0;

        const interval = setInterval((): void => {
            if (count <= placeholderText.length) {
                setPlaceholder(placeholderText.slice(0, count));
                count++;
            } else {
                clearInterval(interval);
            }
        }, 70);

        return (): void => clearInterval(interval);
    }, [placeholderText]);

    return placeholder;
};

export default function HeaderSearchInput({ trackerId, hostname, initialSearchQuery }: HeaderSearchInputProperties): React.JSX.Element {
    const { t } = useTranslation("header");
    const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
    const [isOpenedLuigisboxAutocomplete, setIsOpenedLuigisboxAutocomplete] = useState<boolean>(false);

    const inputReference = useRef<HTMLInputElement>(null);

    const handleClearSearchQuery = (): void => {
        setSearchQuery("");
        inputReference.current?.focus();
        setIsOpenedLuigisboxAutocomplete(false);
    };

    const placeholderText = usePlaceholderTyping(t("searchPlaceholder"));

    const handleInputClick = (): void => {
        setIsOpenedLuigisboxAutocomplete(true);
    };

    const hideLuigisboxAutocomplete = useCallback((): void => {
        setIsOpenedLuigisboxAutocomplete(false);
    }, []);

    const portalContainer = elementService.querySelector<HTMLElement>("#js-luigisbox-autocomplete-header-container");

    return (
        <>
            <input
                type="text"
                name="q"
                className="form-control"
                value={searchQuery}
                onClick={handleInputClick}
                readOnly
                placeholder={placeholderText}
                ref={inputReference}
                id="search-input"
            />
            <i className="fa-solid fa-search"></i>

            {searchQuery !== "" && (
                <button
                    className="header-search-clear"
                    onClick={handleClearSearchQuery}
                    type="reset"
                    aria-label={t("clearText")}
                >
                    <i className="fa-solid fa-times-circle"></i>
                </button>
            )}

            {createPortal(
                <LuigisboxAutocomplete
                    trackerId={trackerId}
                    hostname={hostname}
                    query={searchQuery}
                    onQueryChange={setSearchQuery}
                    isOpened={isOpenedLuigisboxAutocomplete}
                    onHide={hideLuigisboxAutocomplete}
                />,
                portalContainer,
            )}
        </>
    );
}
